import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { 
  FaFilter, FaMagic, FaSort, FaFont, FaCut, 
  FaCheckCircle, FaCopy, FaCheck, FaListOl, FaPercentage 
} from 'react-icons/fa';
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";


const RemoveDuplicate = () => {

  const { darkMode } = useTheme();
  const [inputText, setInputText] = useState('');
     
  const [sortOption, setSortOption] = useState('none');
  const [caseOption, setCaseOption] = useState('sensitive');
  const [trimOption, setTrimOption] = useState('trim');
  const [resultContent, setResultContent] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [originalCount, setOriginalCount] = useState(0);
  const [uniqueCount, setUniqueCount] = useState(0);
  const [reductionPercent, setReductionPercent] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem('dedupeExampleShown')) {
      setInputText(`john@example.com
jane@example.com
John@example.com
jane@example.com
alice@example.com
bob@example.com
john@example.com`);
      localStorage.setItem('dedupeExampleShown', 'true');
    }
  }, []);

  const processInput = () => {
    const text = inputText;
    const sort = sortOption;
    const caseSensitive = caseOption === 'sensitive';
    const trim = trimOption === 'trim';

    if (!text.trim()) {
      setShowResult(false);
      return;
    }

    let items = text.split('\n');
    
    if (trim) {
      items = items.map(item => item.trim());
    }
    
    items = items.filter(item => item !== '');
    
    const originalItems = items.length;
    
    let uniqueItems;
    if (caseSensitive) {
      uniqueItems = [...new Set(items)];
    } else {
      const lowerCaseMap = new Map();
      items.forEach(item => {
        lowerCaseMap.set(item.toLowerCase(), item);
      });
      uniqueItems = Array.from(lowerCaseMap.values());
    }
    
    if (sort === 'alphabetical') {
      uniqueItems.sort();
    } else if (sort === 'reverse-alphabetical') {
      uniqueItems.sort().reverse();
    }
    
    const reduction = originalItems > 0 
      ? Math.round(((originalItems - uniqueItems.length) / originalItems) * 100)
      : 0;
    
    setResultContent(uniqueItems.join('\n'));
    setShowResult(true);
    setOriginalCount(originalItems);
    setUniqueCount(uniqueItems.length);
    setReductionPercent(reduction);
  };

  const copyToClipboard = () => {
    if (!resultContent) return;
    
    navigator.clipboard.writeText(resultContent)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const clearAll = () => {
    setInputText('');
    setResultContent('');
    setShowResult(false);
    setOriginalCount(0);
    setUniqueCount(0);
    setReductionPercent(0);
  };

  return (
    <>
      <HeaderSection toolId="removeDuplicateElements" />
      
      <div className="max-w-4xl mx-auto p-4 lg:max-w-full lg:px-4">
        <div className={`rounded-xl shadow-lg border transition-colors duration-300 ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white rounded-t-xl">
            <h1 className="text-2xl font-bold mb-2 flex items-center">
              <FontAwesomeIcon icon={faFilter} className="mr-3" />
              Remove Duplicate Elements
            </h1>
            <p className="opacity-90">
              Remove duplicate elements from any list (emails, phone numbers, text) with advanced filtering options
            </p>
          </div>

          <div className="p-6">
            {/* Input section */}
            <section className="mb-6">
              <label className={`block mb-2 font-medium ${
                darkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Your list (one item per line)
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                className={`w-full h-48 p-4 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                  darkMode ? 
                    'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 
                    'bg-white border-gray-300 text-gray-800 placeholder-gray-500'
                }`}
                placeholder="Paste your items here, each on a new line..."
              />
            </section>

            {/* Controls */}
            <section className="flex flex-wrap gap-4 mb-6 justify-center">
              <div className="flex items-center gap-2">
                <label htmlFor="sortOption" className={`flex items-center gap-2 text-sm font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <FaSort className={darkMode ? 'text-gray-400' : 'text-gray-500'} /> Sort:
                </label>
                <select
                  id="sortOption"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  className={`px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                    darkMode ? 
                      'bg-gray-700 border-gray-600 text-gray-100' : 
                      'bg-white border-gray-300 text-gray-800'
                  }`}
                >
                  <option value="none">No sorting</option>
                  <option value="alphabetical">A → Z</option>
                  <option value="reverse-alphabetical">Z → A</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label htmlFor="caseOption" className={`flex items-center gap-2 text-sm font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <FaFont className={darkMode ? 'text-gray-400' : 'text-gray-500'} /> Case:
                </label>
                <select
                  id="caseOption"
                  value={caseOption}
                  onChange={(e) => setCaseOption(e.target.value)}
                  className={`px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                    darkMode ? 
                      'bg-gray-700 border-gray-600 text-gray-100' : 
                      'bg-white border-gray-300 text-gray-800'
                  }`}
                >
                  <option value="sensitive">Case sensitive</option>
                  <option value="insensitive">Case insensitive</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label htmlFor="trimOption" className={`flex items-center gap-2 text-sm font-medium ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <FaCut className={darkMode ? 'text-gray-400' : 'text-gray-500'} /> Trim:
                </label>
                <select
                  id="trimOption"
                  value={trimOption}
                  onChange={(e) => setTrimOption(e.target.value)}
                  className={`px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all ${
                    darkMode ? 
                      'bg-gray-700 border-gray-600 text-gray-100' : 
                      'bg-white border-gray-300 text-gray-800'
                  }`}
                >
                  <option value="trim">Trim whitespace</option>
                  <option value="no-trim">Keep whitespace</option>
                </select>
              </div>
            </section>

            {/* Action buttons */}
            <section className="flex flex-wrap gap-3 mb-6">
              <button
                onClick={processInput}
                disabled={!inputText.trim()}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold flex items-center justify-center gap-3 transition-all duration-300 ${
                  !inputText.trim() ? 
                    'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' : 
                    'bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg'
                }`}
              >
                <FaMagic className="transition-transform duration-300 group-hover:rotate-12" />
                Remove Duplicates
              </button>
              
              <button
                onClick={clearAll}
                className="py-3 px-6 rounded-lg font-medium border transition-colors duration-300 flex items-center justify-center gap-2 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                Clear All
              </button>
            </section>

            {/* Result section */}
            {showResult && (
              <section className={`p-5 rounded-lg border transition-all duration-300 ${
                darkMode ? 
                  'bg-gray-700/50 border-gray-600' : 
                  'bg-green-50 border-green-200'
              }`}>
                <div className="flex justify-between items-center mb-4">
                  <h2 className={`text-lg font-medium flex items-center gap-2 ${
                    darkMode ? 'text-green-400' : 'text-green-600'
                  }`}>
                    <FaCheckCircle />
                    Filtered Results
                  </h2>
                  <button
                    onClick={copyToClipboard}
                    className={`py-2 px-4 rounded-md flex items-center gap-2 transition-colors duration-200 ${
                      copied ? 
                        'bg-emerald-600 text-white' : 
                        darkMode ? 
                          'bg-gray-600 hover:bg-gray-500 text-gray-200' : 
                          'bg-emerald-500 hover:bg-emerald-600 text-white'
                    }`}
                  >
                    {copied ? <FaCheck /> : <FaCopy />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                
                <pre className={`whitespace-pre-wrap font-sans p-3 rounded mb-4 overflow-auto max-h-64 ${
                  darkMode ? 
                    'bg-gray-800 text-gray-200' : 
                    'bg-white text-gray-800'
                }`}>
                  {resultContent}
                </pre>
                
                {/* Stats */}
                <div className={`flex flex-wrap justify-between pt-4 border-t ${
                  darkMode ? 'border-gray-600 text-gray-300' : 'border-green-200 text-gray-600'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <FaListOl className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                    Original: <span className={`font-semibold ${
                      darkMode ? 'text-green-400' : 'text-green-600'
                    }`}>{originalCount}</span> items
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <FaFilter className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                    Unique: <span className={`font-semibold ${
                      darkMode ? 'text-green-400' : 'text-green-600'
                    }`}>{uniqueCount}</span> items
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <FaPercentage className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                    Reduced: <span className={`font-semibold ${
                      darkMode ? 'text-green-400' : 'text-green-600'
                    }`}>{reductionPercent}%</span>
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>

      <BottomSection toolId="removeDuplicateElements" />
    </>
  );
};

export default RemoveDuplicate;